import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    TextField,
    List,
    ListItem,
    Button,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    AutoAwesome as AutoAwesomeIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { SessionQuestionEntityFrontend, AnswerTypeFrontend, BulkUpdateAnswerPayloadItem, WorkerDtoFrontend } from '../../api/sessionQuestionsApi';
import { categorizeSessionQuestions, CategorizedData, CategorizedQuestion } from '../../utils/cvPromptUtils';

export interface ContactInfo {
    name: string;
    email: string;
    phone: string;
}

export interface PromptConfig {
    categoryToggles: { personal: boolean; professional: boolean; other: boolean; professionContext: boolean };
    questionOverrides: Array<{ id: number; answer: string }>;
    questionToggles: Record<number, boolean>;
    extraInstructions: string;
    language: string;
    contactInfo: ContactInfo;
    theme: string;
    includeQuestionnairePage: boolean;
}

const LANGUAGE_OPTIONS = [
    { value: 'NL', label: 'Nederlands' },
    { value: 'EN', label: 'English' },
    { value: 'DE', label: 'Deutsch' },
    { value: 'PL', label: 'Polski' },
];

export const THEME_OPTIONS = [
    { value: 'modern-green', label: 'Modern Green' },
    { value: 'classic-black', label: 'Classic Black' },
    { value: 'minimal-gray', label: 'Minimal Gray' },
];

const SAVABLE_ANSWER_TYPES = new Set<string>([
    AnswerTypeFrontend.TEXT,
    AnswerTypeFrontend.TEXT_AREA,
    AnswerTypeFrontend.NUMBER,
    AnswerTypeFrontend.DATE,
]);

const DEBOUNCE_MS = 400;

type CategoryKey = 'personalInfo' | 'professionalInfo' | 'otherInfo' | 'professionContext';

const CATEGORY_CONFIG: { key: CategoryKey; toggleKey: keyof PromptConfig['categoryToggles']; label: string }[] = [
    { key: 'personalInfo', toggleKey: 'personal', label: 'Personal Info' },
    { key: 'professionalInfo', toggleKey: 'professional', label: 'Professional Info' },
    { key: 'otherInfo', toggleKey: 'other', label: 'Other Info' },
    { key: 'professionContext', toggleKey: 'professionContext', label: 'Profession Context' },
];

// ============================================================================
// QuestionItem - Memoized single question with isolated local state
// ============================================================================
interface QuestionItemProps {
    question: CategorizedQuestion;
    enabled: boolean;
    onCommit: (questionId: number, value: string) => void;
    onToggle: (questionId: number) => void;
}

const QuestionItem = memo<QuestionItemProps>(({ question, enabled, onCommit, onToggle }) => {
    const [localValue, setLocalValue] = useState(question.formattedAnswer);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setLocalValue(question.formattedAnswer);
    }, [question.formattedAnswer]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalValue(value);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            onCommit(question.id, value);
        }, DEBOUNCE_MS);
    }, [question.id, onCommit]);

    const handleBlur = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        onCommit(question.id, localValue);
    }, [question.id, localValue, onCommit]);

    return (
        <ListItem
            sx={{
                px: 0,
                py: 0.5,
                alignItems: 'flex-start',
                opacity: enabled ? 1 : 0.45,
            }}
        >
            <Switch
                size="small"
                checked={enabled}
                onChange={() => onToggle(question.id)}
                sx={{ mt: 0.5, mr: 1, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        color: enabled ? 'text.primary' : 'text.disabled',
                        mb: 0.5,
                    }}
                >
                    {question.label}
                </Typography>
                <TextField
                    variant="standard"
                    size="small"
                    fullWidth
                    multiline
                    maxRows={3}
                    value={localValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!enabled}
                    sx={{ '& .MuiInput-root': { fontSize: '0.85rem' } }}
                />
            </Box>
        </ListItem>
    );
});

QuestionItem.displayName = 'QuestionItem';

// ============================================================================
// CategoryAccordion - Memoized category with question items
// ============================================================================
interface CategoryAccordionProps {
    config: (typeof CATEGORY_CONFIG)[number];
    items: CategorizedQuestion[];
    categoryEnabled: boolean;
    questionToggles: Record<number, boolean>;
    onCategoryToggle: (key: keyof PromptConfig['categoryToggles']) => void;
    onQuestionToggle: (questionId: number) => void;
    onQuestionCommit: (questionId: number, value: string) => void;
}

const CategoryAccordion = memo<CategoryAccordionProps>(({
    config,
    items,
    categoryEnabled,
    questionToggles,
    onCategoryToggle,
    onQuestionToggle,
    onQuestionCommit,
}) => {
    if (items.length === 0) return null;

    return (
        <Accordion defaultExpanded={items.length <= 10}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                onClick={(e) => {
                    if ((e.target as HTMLElement).closest('.cfg-cat-checkbox')) {
                        e.stopPropagation();
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Checkbox
                        className="cfg-cat-checkbox"
                        checked={categoryEnabled}
                        onChange={(e) => {
                            e.stopPropagation();
                            onCategoryToggle(config.toggleKey);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ mr: 1 }}
                    />
                    <Typography variant="subtitle1" sx={{ userSelect: 'none' }}>
                        {config.label} ({items.length})
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <List dense disablePadding>
                    {items.map((q) => {
                        const qEnabled = categoryEnabled && (questionToggles[q.id] ?? true);
                        return (
                            <QuestionItem
                                key={q.id}
                                question={q}
                                enabled={qEnabled}
                                onCommit={onQuestionCommit}
                                onToggle={onQuestionToggle}
                            />
                        );
                    })}
                </List>
            </AccordionDetails>
        </Accordion>
    );
});

CategoryAccordion.displayName = 'CategoryAccordion';

// ============================================================================
// Main Component - Orchestrator only
// ============================================================================
interface CVPromptConfigPanelProps {
    sessionQuestions: SessionQuestionEntityFrontend[];
    worker: WorkerDtoFrontend | null;
    onGenerate: (config: PromptConfig) => void;
    loading?: boolean;
    initialConfig?: PromptConfig | null;
    onConfigChange?: (config: PromptConfig) => void;
    onSaveEdits?: (updates: BulkUpdateAnswerPayloadItem[]) => Promise<void> | void;
    savingEdits?: boolean;
}

const CVPromptConfigPanel: React.FC<CVPromptConfigPanelProps> = ({
    sessionQuestions,
    worker,
    onGenerate,
    loading = false,
    initialConfig,
    onConfigChange,
    onSaveEdits,
    savingEdits = false,
}) => {
    // ========== Top-level state: only orchestration-level config ==========
    const [categoryToggles, setCategoryToggles] = useState<PromptConfig['categoryToggles']>(
        initialConfig?.categoryToggles ?? { personal: true, professional: true, other: true, professionContext: true }
    );
    const [extraInstructions, setExtraInstructions] = useState(initialConfig?.extraInstructions ?? '');
    const [language, setLanguage] = useState(initialConfig?.language ?? 'NL');
    const [theme, setTheme] = useState(initialConfig?.theme ?? 'modern-green');
    const [contactInfo, setContactInfo] = useState<ContactInfo>(
        initialConfig?.contactInfo ?? { name: '', email: '', phone: '' }
    );
    const [includeQuestionnairePage, setIncludeQuestionnairePage] = useState<boolean>(
        initialConfig?.includeQuestionnairePage ?? true
    );
    const [categorizedData, setCategorizedData] = useState<CategorizedData>({
        personalInfo: [],
        professionalInfo: [],
        otherInfo: [],
        professionContext: [],
    });
    const [editedAnswers, setEditedAnswers] = useState<Record<number, string>>({});
    const [questionToggles, setQuestionToggles] = useState<Record<number, boolean>>(
        initialConfig?.questionToggles ?? {}
    );

    // ========== Effects ==========
    useEffect(() => {
        setCategorizedData(categorizeSessionQuestions(sessionQuestions, worker));
    }, [sessionQuestions, worker]);

    useEffect(() => {
        const incoming = (initialConfig?.questionOverrides ?? []) as Array<{ id: number; answer: string }>;
        setEditedAnswers(prev => {
            const incomingMap = new Map(incoming.map((ov: { id: number; answer: string }) => [ov.id, ov.answer]));
            const next: Record<number, string> = { ...prev };
            for (const [id, answer] of incomingMap) {
                next[id as number] = answer as string;
            }
            return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
        });
    }, [initialConfig?.questionOverrides]);

    // Only emit config on non-answer-edit changes
    useEffect(() => {
        if (!onConfigChange) return;
        const questionOverrides = Object.entries(editedAnswers).map(([id, answer]) => ({
            id: Number(id),
            answer,
        }));
        onConfigChange({
            categoryToggles,
            questionOverrides,
            questionToggles,
            extraInstructions,
            language,
            contactInfo,
            theme,
            includeQuestionnairePage,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryToggles, questionToggles, extraInstructions, language, contactInfo, theme, includeQuestionnairePage]);

    // ========== Memoized calculations (only when dependencies change) ==========
    const dirtyEdits = useMemo(() => {
        const answerTypeById = new Map<number, string>();
        const originalById = new Map<number, string>();
        const labelById = new Map<number, string>();

        const allCats = [
            ...categorizedData.personalInfo,
            ...categorizedData.professionalInfo,
            ...categorizedData.otherInfo,
            ...categorizedData.professionContext,
        ];

        for (const q of allCats) {
            originalById.set(q.id, q.formattedAnswer);
            labelById.set(q.id, q.label);
        }

        for (const q of sessionQuestions) {
            if (q.id != null) answerTypeById.set(q.id, (q.answerType || '').toUpperCase());
        }

        const out: Array<{ id: number; answer: string; safe: boolean; label: string }> = [];
        for (const [idStr, answer] of Object.entries(editedAnswers)) {
            const id = Number(idStr);
            const original = originalById.get(id);
            if (original === undefined || answer === original) continue;
            const safe = SAVABLE_ANSWER_TYPES.has(answerTypeById.get(id) || '');
            out.push({ id, answer, safe, label: labelById.get(id) || `Question ${id}` });
        }
        return out;
    }, [editedAnswers, categorizedData, sessionQuestions]);

    const savableEdits = useMemo(() => dirtyEdits.filter(d => d.safe), [dirtyEdits]);
    const nonSavableCount = dirtyEdits.length - savableEdits.length;

    // ========== Handlers ==========
    const handleQuestionCommit = useCallback((questionId: number, value: string) => {
        setEditedAnswers(prev => {
            if (prev[questionId] === value) return prev;
            return { ...prev, [questionId]: value };
        });
    }, []);

    const handleToggleCategory = useCallback((toggleKey: keyof PromptConfig['categoryToggles']) => {
        setCategoryToggles(prev => ({ ...prev, [toggleKey]: !prev[toggleKey] }));
    }, []);

    const handleToggleQuestion = useCallback((questionId: number) => {
        setQuestionToggles(prev => ({
            ...prev,
            [questionId]: !(prev[questionId] ?? true),
        }));
    }, []);

    const handleContactChange = useCallback((field: keyof ContactInfo, value: string) => {
        setContactInfo(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSaveEditsClick = useCallback(async () => {
        if (!onSaveEdits || savableEdits.length === 0) return;
        await onSaveEdits(savableEdits.map(({ id, answer }) => ({ sessionQuestionId: id, answer })));
    }, [onSaveEdits, savableEdits]);

    const handleGenerate = useCallback(() => {
        const questionOverrides = Object.entries(editedAnswers).map(([id, answer]) => ({
            id: Number(id),
            answer,
        }));
        onGenerate({
            categoryToggles,
            questionOverrides,
            questionToggles,
            extraInstructions,
            language,
            contactInfo,
            theme,
            includeQuestionnairePage,
        });
    }, [categoryToggles, questionToggles, extraInstructions, language, contactInfo, theme, includeQuestionnairePage, editedAnswers, onGenerate]);

    // ========== Render ==========
    return (
        <Box sx={{ p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                    <Typography variant="h6">Configure AI Input</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Review and edit the data that will be sent to AI for CV generation. Toggle categories and individual questions on/off.
                    </Typography>
                </Box>
                {dirtyEdits.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        {nonSavableCount > 0 && (
                            <Tooltip title="Edits to multi-choice / list / boolean answers can't be persisted as plain text and will only affect this CV's AI generation.">
                                <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                                    {nonSavableCount} not savable
                                </Typography>
                            </Tooltip>
                        )}
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={savingEdits ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveEditsClick}
                            disabled={savingEdits || savableEdits.length === 0 || !onSaveEdits}
                        >
                            {savingEdits ? 'Saving…' : `Save ${savableEdits.length} edit${savableEdits.length === 1 ? '' : 's'} to worker`}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Config Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>CV Language</InputLabel>
                    <Select value={language} label="CV Language" onChange={(e) => setLanguage(e.target.value as string)}>
                        {LANGUAGE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Theme</InputLabel>
                    <Select value={theme} label="Theme" onChange={(e) => setTheme(e.target.value as string)}>
                        {THEME_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Contact Person"
                    placeholder="Name"
                    value={contactInfo.name}
                    onChange={(e) => handleContactChange('name', e.target.value)}
                    sx={{ flex: 1, minWidth: 140 }}
                />
                <TextField
                    size="small"
                    label="Email"
                    placeholder="email@example.com"
                    value={contactInfo.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                    sx={{ flex: 1, minWidth: 140 }}
                />
                <TextField
                    size="small"
                    label="Phone"
                    placeholder="+31 6 12345678"
                    value={contactInfo.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    sx={{ flex: 1, minWidth: 130 }}
                />
            </Box>

            {/* Questionnaire Page Toggle */}
            <Box sx={{ mb: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeQuestionnairePage}
                            onChange={(e) => setIncludeQuestionnairePage(e.target.checked)}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Include Questionnaire Appendix (page 2)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Adds an extra page summarising every answered question. Disable for a shorter, customer-facing CV.
                            </Typography>
                        </Box>
                    }
                />
            </Box>

            {/* Categories Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {CATEGORY_CONFIG.map((config) => (
                    <CategoryAccordion
                        key={config.key}
                        config={config}
                        items={categorizedData[config.key]}
                        categoryEnabled={categoryToggles[config.toggleKey]}
                        questionToggles={questionToggles}
                        onCategoryToggle={handleToggleCategory}
                        onQuestionToggle={handleToggleQuestion}
                        onQuestionCommit={handleQuestionCommit}
                    />
                ))}
            </Box>

            {/* Extra Instructions */}
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Extra Instructions &amp; Details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Anything you type here is treated as authoritative and woven into the generated
                    story, skills and highlights — use it to add extra facts (e.g. extra work
                    experience) or to steer the tone. Note: this shapes the AI-written text; it does
                    not add a new row to the structured Work Experience or Education sections.
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={6}
                    placeholder="e.g. '2 years experience as a forklift operator at DHL' or 'Emphasize leadership and welding certifications'"
                    value={extraInstructions}
                    onChange={(e) => setExtraInstructions(e.target.value)}
                />
            </Box>

            {/* Generate Button */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleGenerate}
                    disabled={loading}
                    startIcon={<AutoAwesomeIcon />}
                >
                    Generate CV
                </Button>
            </Box>
        </Box>
    );
};

export default CVPromptConfigPanel;
